import { useCallback, useEffect, useRef, useState } from 'react';
import {
  rp, now, AG, WHN, SUP, STG, stLabel,
  OPEN, ORD0, PO0, RIDERS, TRUCKS, SHP0, FEED0, SC, SEED,
  SCHAT0, SSC, SDEF, initInv, shipIdx,
} from './data';

const PO_LIMIT = 2500000;

// ---- pure helpers: always take an explicit state snapshot `s`, never
// close over anything, so they give correct answers whether called from
// a render (reactive `state`) or from inside a deferred setTimeout
// (`stateRef.current`, always fresh regardless of when the timer fires). ----
const item = (s, sku) => s.inv.find((i) => i.sku === sku);
const avail = (s, sku) => {
  const i = item(s, sku);
  return i.onHand - i.reserved;
};
const totals = (s, o) => {
  const sub = o.items.reduce((a, l) => a + l.qty * item(s, l.sku).price, 0);
  const disc = o.disc || 0;
  const ship = o.channel === 'branch' || sub - disc >= 150000 ? 0 : 10000;
  return { sub, disc, ship, total: sub - disc + ship };
};
const poTotal = (s, p) => p.items.reduce((a, l) => a + l.qty * item(s, l.sku).cost, 0);
const conv = (s, id) => s.convs.find((c) => c.id === id);
const pendingOrder = (s) => s.orders.find((o) => o.convId === s.activeId && o.stage === 'new');
const itemsStr = (s, items) => items.map((l) => `${item(s, l.sku).name.split(' · ')[0]} ×${l.qty}`).join(', ');
// `oseq` is passed in explicitly (the live counter at call time) rather
// than read from state, since state never tracks it — only the `seq` ref
// in the hook does, and it must reflect the *next* id, not a stale one.
const resolve = (s, v, oseq) => (typeof v === 'function' ? v(mkT(s, oseq)) : v);
// The `t` helper object the original passed into SC step/reply functions.
const mkT = (s, oseq) => ({ avail: (sku) => avail(s, sku), totals: (o) => totals(s, o), pendingOrder: () => pendingOrder(s), oseq });

// SEED is mutated (messages pushed) over the session — copy so a Reset
// truly starts over rather than compounding across mounts.
const SEED_COPY = () => SEED.map((c) => ({ ...c, messages: [...c.messages] }));

const initState = () => ({
  role: 'staff', supId: 's1', view: 'overview', activeId: 'maya', tab: 'agent',
  channel: 'all', status: 'all', q: '', draft: '', convs: SEED_COPY(), busy: false, typing: null, off: {},
  orders: ORD0.map((o) => ({ ...o })), inv: initInv(), pos: PO0.map((p) => ({ ...p })), feed: [...FEED0],
  selOrder: 'GR-2045', stageF: 'all', whF: 'all', toast: null,
  ships: SHP0.map((s) => ({ ...s })), selShip: 'SHP-7779', shipF: 'all',
  schats: { ...SCHAT0 }, sDraft: '', sTyping: false, exc: [], leak: 4200000,
  score: { s1: 87, s2: 74, s3: 79, s4: 91 }, guideOpen: true,
});

export default function useTbsDemoState() {
  const [state, setState] = useState(initState);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const timers = useRef([]);
  const toastTimer = useRef(null);
  const seq = useRef({ oseq: 2051, pseq: 421, wave: 14, dseq: 0 });
  const threadRef = useRef(null);
  const sRef = useRef(null);

  // Mirrors class setState: accepts a partial object OR an updater
  // function, always shallow-merged onto the previous state — and, via
  // the functional form, always sees the latest state regardless of when
  // it actually runs (the one guarantee this whole port leans on).
  const patch = useCallback((objOrFn) => {
    setState((prev) => {
      const partial = typeof objOrFn === 'function' ? objOrFn(prev) : objOrFn;
      return { ...prev, ...partial };
    });
  }, []);

  const after = useCallback((ms, fn) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  }, []);

  useEffect(() => {
    const iv = setInterval(() => tick(), 2500);
    return () => {
      timers.current.forEach(clearTimeout);
      clearInterval(iv);
      clearTimeout(toastTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [state.convs, state.activeId, state.typing, state.view]);

  useEffect(() => {
    if (sRef.current) sRef.current.scrollTop = sRef.current.scrollHeight;
  }, [state.schats, state.sTyping, state.view, state.supId]);

  const reset = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    seq.current = { oseq: 2051, pseq: 421, wave: 14, dseq: 0 };
    setState(initState());
  }, []);

  const addFeed = useCallback((agent, sys, text) => {
    patch((s) => ({ feed: [{ agent, sys, text, time: now() }, ...s.feed].slice(0, 50) }));
  }, [patch]);

  const toastMsg = useCallback((text, agent) => {
    clearTimeout(toastTimer.current);
    patch({ toast: { text, agent } });
    toastTimer.current = setTimeout(() => patch({ toast: null }), 4000);
  }, [patch]);

  const upd = useCallback((id, fn) => {
    patch((s) => ({ convs: s.convs.map((c) => (c.id === id ? fn(c) : c)) }));
  }, [patch]);

  const push = useCallback((id, msg, extra) => {
    upd(id, (c) => ({ ...c, ...(extra || {}), messages: [...c.messages, msg] }));
  }, [upd]);

  const setPO = useCallback((id, p) => {
    patch((s) => ({ pos: s.pos.map((x) => (x.id === id ? { ...x, ...p } : x)) }));
  }, [patch]);

  const orderEvent = useCallback((id, agent, text) => {
    patch((s) => ({ orders: s.orders.map((o) => (o.id === id ? { ...o, events: [...o.events, { agent, text, time: now() }] } : o)) }));
  }, [patch]);

  const pushS = useCallback((sup, msg) => {
    patch((s) => ({ schats: { ...s.schats, [sup]: [...(s.schats[sup] || []), { time: now(), ...msg }] } }));
  }, [patch]);

  const createOrder = useCallback((convId) => {
    const s = stateRef.current;
    const c = conv(s, convId);
    const id = 'GR-' + seq.current.oseq++;
    const ord = { id, convId, customer: c.name, channel: c.channel, stage: 'new', time: now(), items: [{ sku: 'APL', qty: 3 }], disc: 13500, events: [{ agent: 'sari', text: 'Order created from WhatsApp · promo 3 kg −10%', time: now() }] };
    patch((prev) => ({ orders: [ord, ...prev.orders], inv: prev.inv.map((i) => (i.sku === 'APL' ? { ...i, reserved: i.reserved + 3 } : i)), selOrder: id }));
    addFeed('sari', 'Orders', `Created ${id} for ${c.name} · Rp131.500`);
    after(700, () => {
      addFeed('wira', 'Warehouse', 'Reserved 3 kg Apel Fuji at DC Pulogadung CS-A1 (batch harvested 6 days ago)');
      orderEvent(id, 'wira', 'Stock reserved · PGD CS-A1');
    });
    after(1700, () => reorderCheck(['APL']));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patch, addFeed, after, orderEvent]);

  const createOutShip = useCallback((o) => {
    const s = stateRef.current;
    const dr = RIDERS[seq.current.dseq++ % RIDERS.length];
    const wh = item(s, o.items[0].sku).wh;
    const br = o.channel === 'branch';
    const sh = { id: 'SHP-' + (7790 + seq.current.dseq), dir: 'out', ref: o.id, party: o.customer, carrier: br ? 'TBS outlet truck' : 'TBS rider · motor', driver: dr[0], plate: dr[1], phone: dr[2], from: WHN[wh], to: br ? o.customer : dr[3], progress: 0, pings: [{ t: now(), x: `Picked up at ${WHN[wh]}` }] };
    patch((prev) => ({ ships: [sh, ...prev.ships], selShip: sh.id }));
    addFeed('wira', 'Dispatch', `Booked ${dr[0]} (${dr[1]}) for ${o.id} → ${sh.to}`);
    if (o.convId && !br) after(600, () => push(o.convId, { type: 'ai', agent: 'sari', text: `Pesanan ${o.id} sudah lolos cek kualitas dan dibawa rider ${dr[0].split(' ')[0]}. Lacak: track.totalbuah.id/${o.id}`, time: now() }));
  }, [patch, addFeed, after, push]);

  const advanceOrder = useCallback((id) => {
    const s = stateRef.current;
    const o = s.orders.find((x) => x.id === id);
    if (!o) return;
    const i = STG.findIndex((st) => st.k === o.stage);
    if (i >= STG.length - 1) return;
    const nx = STG[i + 1];
    const text = nx.k === 'shipped' ? 'Rider booked · live tracking on' : nx.ev;
    patch((prev) => ({
      orders: prev.orders.map((x) => (x.id === id ? { ...x, stage: nx.k, events: [...x.events, { agent: nx.agent, text, time: now() }] } : x)),
      inv: nx.k === 'packed' ? prev.inv.map((it) => { const li = o.items.find((l) => l.sku === it.sku); return li ? { ...it, onHand: it.onHand - li.qty, reserved: Math.max(0, it.reserved - li.qty) } : it; }) : prev.inv,
    }));
    addFeed(nx.agent, ['paid', 'delivered'].includes(nx.k) ? 'Orders' : nx.k === 'shipped' ? 'Dispatch' : 'Warehouse', `${id} → ${nx.l} · ${text}`);
    if (nx.k === 'packed') after(900, () => reorderCheck(o.items.map((l) => l.sku)));
    if (nx.k === 'shipped') createOutShip(o);
    if (nx.k === 'delivered') patch((prev) => ({ ships: prev.ships.map((sh) => (sh.ref === id && sh.progress < 100 ? { ...sh, progress: 100, pings: [...sh.pings, { t: now(), x: 'Delivered · confirmed manually' }] } : sh)) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patch, addFeed, after, createOutShip]);

  const pickWave = useCallback(() => {
    const s = stateRef.current;
    const paid = s.orders.filter((o) => o.stage === 'paid');
    if (!paid.length) { toastMsg('No paid orders waiting to be picked', 'wira'); return; }
    const w = 'W-' + seq.current.wave++;
    addFeed('wira', 'Warehouse', `Started FEFO wave ${w} · ${paid.map((o) => o.id).join(', ')}`);
    toastMsg(`Wira started FEFO wave ${w} · ${paid.length} order${paid.length > 1 ? 's' : ''}`, 'wira');
    paid.forEach((o, k) => [0, 1, 2].forEach((j) => after(700 + k * 450 + j * 1300, () => advanceOrder(o.id))));
  }, [addFeed, toastMsg, after, advanceOrder]);

  const reorderCheck = useCallback((skus) => {
    const s = stateRef.current;
    const openSkus = new Set(s.pos.filter((p) => OPEN.includes(p.status)).flatMap((p) => p.items.map((i) => i.sku)));
    const cand = s.inv.filter((i) => (!skus || skus.includes(i.sku)) && i.onHand - i.reserved <= i.rop && !openSkus.has(i.sku));
    if (!cand.length) { if (!skus) toastMsg(`Tara checked ${s.inv.length} SKUs · all covered by stock or open POs`, 'tara'); return; }
    const groups = {};
    cand.forEach((i) => { (groups[i.sup] = groups[i.sup] || []).push(i); });
    Object.entries(groups).forEach(([sup, list], k) => {
      const id = 'PO-0' + seq.current.pseq++;
      const L = SUP[sup].lead;
      const items = list.map((i) => ({ sku: i.sku, qty: Math.ceil(Math.max(i.daily * (L + 4) - (i.onHand - i.reserved), i.daily * 3) / 10) * 10 }));
      const total = items.reduce((a, l) => a + l.qty * item(s, l.sku).cost, 0);
      const auto = total <= PO_LIMIT;
      const f = list[0];
      const why = `${f.onHand - f.reserved} available ≤ reorder ${f.rop} · ${f.daily}/day · lead ${L}d`;
      after(k * 500, () => {
        patch((prev) => ({ pos: [{ id, sup, items, status: auto ? 'approved' : 'draft', created: 'Today ' + now(), eta: '—', why }, ...prev.pos] }));
        addFeed('tara', 'Procurement', auto ? `Auto-approved ${id} · ${itemsStr(s, items)} · ${rp(total)} (under limit)` : `Drafted ${id} · ${itemsStr(s, items)} · ${rp(total)} — needs buyer approval`);
        toastMsg(auto ? `Tara auto-sent ${id} to ${SUP[sup].name}` : `Tara drafted ${id} · ${rp(total)} — needs your approval`, 'tara');
        if (auto) poFlow(id);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patch, addFeed, toastMsg, after]);

  const poFlow = useCallback((id) => {
    after(1200, () => {
      const s = stateRef.current;
      const po = s.pos.find((x) => x.id === id);
      const sup = SUP[po.sup];
      setPO(id, { status: 'sent', eta: 'Awaiting confirmation' });
      addFeed('tara', 'Procurement', `Sent ${id} to ${sup.name} on WhatsApp · chasing confirmation`);
      pushS(po.sup, { from: 'tara', text: `PO baru ${id}: ${itemsStr(s, po.items)} · ${rp(poTotal(s, po))}. Mohon konfirmasi ya ${sup.contact.split(' ')[0] === 'Pak' || sup.contact.split(' ')[0] === 'Bu' ? sup.contact : 'Bu/Pak ' + sup.contact.split(' ')[0]}.` });
    });
  }, [after, setPO, addFeed, pushS]);

  const approvePO = useCallback((id) => {
    setPO(id, { status: 'approved', why: 'Approved by Johan' });
    addFeed('tara', 'Procurement', `${id} approved by Johan · sending to supplier`);
    poFlow(id);
  }, [setPO, addFeed, poFlow]);

  const rejectPO = useCallback((id) => {
    setPO(id, { status: 'rejected' });
    addFeed('tara', 'Procurement', `${id} rejected by Johan`);
  }, [setPO, addFeed]);

  const supConfirm = useCallback((id, partial) => {
    const s = stateRef.current;
    const p = s.pos.find((x) => x.id === id);
    const sup = SUP[p.sup];
    const L = sup.lead;
    const eta = L <= 1 ? 'Tomorrow 06:00' : `In ${L} days`;
    const items = partial ? p.items.map((l) => ({ ...l, qty: Math.max(10, Math.round((l.qty * 0.8) / 10) * 10) })) : p.items;
    setPO(id, { status: 'confirmed', eta, items, why: partial ? `Partial ${items[0].qty}/${p.items[0].qty} confirmed · Tara accepted` : p.why });
    addFeed('tara', 'Suppliers', partial ? `${sup.name} confirmed ${id} partially (${items[0].qty} of ${p.items[0].qty}) — Tara accepted, remainder rolls to next PO` : `${sup.name} confirmed ${id} · ETA ${eta}`);
    after(900, () => pushS(p.sup, { from: 'tara', text: partial ? `Konfirmasi parsial ${id} diterima. Sisanya masuk PO berikutnya.` : `Terima kasih, ${id} terkonfirmasi. Silakan dispatch truk — slot dock sudah saya siapkan.` }));
    toastMsg(`${sup.name} confirmed ${id}`, 'tara');
  }, [setPO, addFeed, after, pushS, toastMsg]);

  const supDispatch = useCallback((id) => {
    const s = stateRef.current;
    const p = s.pos.find((x) => x.id === id);
    const sup = SUP[p.sup];
    const dr = TRUCKS[seq.current.dseq++ % TRUCKS.length];
    const wh = item(s, p.items[0].sku).wh;
    const sh = { id: 'SHP-' + (7790 + seq.current.dseq), dir: 'in', ref: id, party: `${sup.name} · ${itemsStr(s, p.items)}`, carrier: 'Reefer truck', driver: dr[0], plate: dr[1], phone: dr[2], from: sup.origin, to: `${WHN[wh]} · Dock 1`, progress: 0, temp: '3.5°C', pings: [{ t: now(), x: `Loading at ${sup.origin} · reefer pre-cooled` }] };
    patch((prev) => ({ ships: [sh, ...prev.ships], selShip: sh.id }));
    setPO(id, { status: 'transit', eta: 'On the way' });
    addFeed('wira', 'Dispatch', `${sup.name} dispatched ${id} · reefer ${dr[1]} · Dock 1 at ${WHN[wh]} reserved`);
    after(900, () => pushS(p.sup, { from: 'tara', text: `Truk ${dr[1]} tercatat untuk ${id}. Dock 1 di ${WHN[wh]} siap, suhu reefer kami pantau.` }));
    toastMsg(`${sup.name} dispatched ${id} · live tracking on`, 'wira');
  }, [patch, setPO, addFeed, after, pushS, toastMsg]);

  const receivePO = useCallback((id) => {
    const s = stateRef.current;
    const p = s.pos.find((x) => x.id === id);
    if (!p) return;
    const short = p.short || 0;
    const l = p.items[0];
    const it = item(s, l.sku);
    const got = l.qty - short;
    setPO(id, { status: 'received', eta: 'Received ' + now(), grn: true, got });
    patch((prev) => ({
      inv: prev.inv.map((i) => { const x = p.items.find((y) => y.sku === i.sku); return x ? { ...i, onHand: i.onHand + x.qty - (x.sku === l.sku ? short : 0) } : i; }),
      ships: prev.ships.map((sh) => (sh.ref === id ? { ...sh, progress: 100, received: true, pings: [...sh.pings, { t: now(), x: `Received · GRN ${got}/${l.qty}` }] } : sh)),
    }));
    addFeed('wira', 'Warehouse', `Received ${id} at dock · counted ${got} of ${l.qty} ${it.name.split(' · ')[0]} · put away ${it.bin}`);
    if (short) {
      after(900, () => {
        const s2 = stateRef.current;
        const ex = { id: 'EX-0' + (31 + s2.exc.length), po: id, sup: p.sup, sku: l.sku, ordered: l.qty, received: got, gap: short, value: short * it.cost, status: 'open' };
        patch((prev) => ({ exc: [ex, ...prev.exc], score: { ...prev.score, [p.sup]: prev.score[p.sup] - 3 } }));
        addFeed('tara', 'Procurement', `Short-ship on ${id}: ordered ${l.qty}, received ${got} (−${short}) · ${rp(ex.value)} at risk — proposing claim`);
        toastMsg(`Tara caught a short-ship on ${id} · −${short} kg`, 'tara');
      });
    } else toastMsg(`Wira received ${id} · stock updated`, 'wira');
    after(1600, () => {
      setPO(id, { invoiced: true });
      addFeed('tara', 'Procurement', `Invoice for ${id} matched to PO, GRN and contract${short ? ' — qty mismatch held for claim' : ''}`);
      if (!short) after(800, () => setPO(id, { status: 'closed' }));
    });
  }, [setPO, patch, addFeed, after, toastMsg]);

  const resolveEx = useCallback((exId, how) => {
    const s = stateRef.current;
    const ex = s.exc.find((e) => e.id === exId);
    if (!ex || ex.status !== 'open') return;
    const sup = SUP[ex.sup];
    const name = item(s, ex.sku).name.split(' · ')[0];
    patch((prev) => ({ exc: prev.exc.map((e) => (e.id === exId ? { ...e, status: how } : e)), leak: how === 'claimed' ? prev.leak + ex.value : prev.leak }));
    if (how === 'claimed') {
      setPO(ex.po, { status: 'closed', claimed: true });
      addFeed('tara', 'Procurement', `Claim raised on ${ex.po} · ${rp(ex.value)} credit note requested from ${sup.name}`);
      toastMsg(`Leakage claimed · ${rp(ex.value)}`, 'tara');
    } else addFeed('tara', 'Suppliers', `Asked ${sup.name} to re-deliver ${ex.gap} kg ${name} tomorrow 06:00`);
    after(800, () => pushS(ex.sup, { from: 'tara', text: how === 'claimed' ? `Kami terima ${ex.received} dari ${ex.ordered} kg ${name} (${ex.po}). Credit note ${rp(ex.value)} sudah kami ajukan, mohon dikonfirmasi.` : `Kami terima ${ex.received} dari ${ex.ordered} kg ${name} (${ex.po}). Mohon kirim sisa ${ex.gap} kg besok 06:00 ke Dock 1.` }));
  }, [patch, setPO, addFeed, toastMsg, after, pushS]);

  const onShipStage = useCallback((n, a) => {
    if (n.dir === 'out') {
      if (a === 2) addFeed('wira', 'Dispatch', `${n.ref} nearby · ${n.driver} is 2 stops away`);
      if (a === 3) {
        const o = stateRef.current.orders.find((x) => x.id === n.ref);
        if (o && o.stage === 'shipped') advanceOrder(o.id);
        toastMsg(`${n.driver} delivered ${n.ref} to ${n.party}`, 'wira');
      }
    } else if (a === 2) {
      setPO(n.ref, { status: 'arrived', eta: 'At dock ' + now() });
      addFeed('wira', 'Dispatch', `${n.plate} arrived at ${n.to} · ${n.ref} ready to receive · reefer ${n.temp}`);
      toastMsg(`${n.ref} reefer is at the dock — ready to receive`, 'wira');
    }
  }, [addFeed, advanceOrder, toastMsg, setPO]);

  const tick = useCallback(() => {
    const ev = [];
    setState((prev) => {
      const ships = prev.ships.map((sh) => {
        if (sh.progress >= 100) return sh;
        const b = shipIdx(sh);
        const n = { ...sh, progress: Math.min(100, sh.progress + (sh.dir === 'out' ? 4 : 3)) };
        const a = shipIdx(n);
        if (a !== b) {
          const x = sh.dir === 'out'
            ? ['Picked up', 'On the way', 'Nearby · 2 stops away', 'Delivered · photo proof captured'][a]
            : ['Loading', `On the way · reefer ${sh.temp}`, `Arrived at dock · reefer ${sh.temp} · waiting for receiving`, 'Received'][a];
          n.pings = [...sh.pings, { t: now(), x }];
          ev.push([n, a]);
        }
        return n;
      });
      return ships.some((x, i) => x !== prev.ships[i]) ? { ...prev, ships } : prev;
    });
    ev.forEach(([n, a]) => onShipStage(n, a));
  }, [onShipStage]);

  const fastForward = useCallback((id) => {
    patch((prev) => ({ ships: prev.ships.map((sh) => (sh.id === id && sh.progress < 97 ? { ...sh, progress: 97 } : sh)) }));
  }, [patch]);

  const setMode = useCallback((mode) => {
    const s = stateRef.current;
    const c = conv(s, s.activeId);
    if (!c || c.mode === mode) return;
    const a = AG[c.agent].name;
    const text = mode === 'manual' ? `You took over from ${a} · AI paused` : mode === 'auto' ? `Handed back to ${a} · autopilot on` : `Copilot on · ${a} drafts, you approve`;
    push(c.id, { type: 'system', text }, { mode, status: c.status === 'human' && mode !== 'auto' ? 'human' : 'ongoing' });
  }, [push]);

  const simulate = useCallback((key, convId) => {
    const s = stateRef.current;
    if (s.busy) return false;
    const sc = SC[key];
    const id = convId || s.activeId;
    const c = conv(s, id);
    if (!c) return false;
    const steps = sc.steps.map(([t, d, sys]) => [t, resolve(s, d, seq.current.oseq), sys]);
    const reply = resolve(s, sc.reply, seq.current.oseq);
    push(id, { type: 'customer', text: sc.label, time: now() }, { preview: sc.label, time: now(), status: c.status === 'closed' ? 'ongoing' : c.status });
    if (c.mode === 'manual') { upd(id, (x) => ({ ...x, unread: (x.unread || 0) + 1 })); return true; }
    patch({ busy: true });
    const rid = 'r' + Date.now();
    after(450, () => push(id, { type: 'run', rid, agent: sc.agent, time: now(), status: 'running', outcome: sc.outcome, conf: sc.conf, steps: steps.map(([tool, detail, sys], i) => ({ tool, detail, sys, state: i === 0 ? 'running' : 'pending' })) }, { agent: sc.agent }));
    const setRun = (fn) => upd(id, (x) => ({ ...x, messages: x.messages.map((m) => (m.rid === rid ? fn(m) : m)) }));
    steps.forEach((_, i) => after(450 + 700 * (i + 1), () => setRun((m) => ({ ...m, steps: m.steps.map((st, j) => ({ ...st, state: j <= i ? 'done' : j === i + 1 ? 'running' : 'pending' })) }))));
    after(450 + 700 * steps.length + 150, () => {
      setRun((m) => ({ ...m, status: 'done' }));
      if (conv(stateRef.current, id).mode === 'copilot') { upd(id, (x) => ({ ...x, suggestion: sc.suggest || reply, suggestBy: sc.agent })); patch({ busy: false }); return; }
      patch({ typing: { id, agent: sc.agent } });
      after(900, () => {
        patch({ typing: null, busy: false });
        push(id, { type: 'ai', agent: sc.agent, text: reply, time: now() }, { preview: reply.split('\n')[0] });
        if (sc.feed) addFeed(sc.agent, 'Inbox', sc.feed);
        if (sc.effect === 'order') createOrder(id);
        if (sc.effect === 'paid') {
          const o = stateRef.current.orders.find((x) => x.convId === id && x.stage === 'new');
          if (o) { advanceOrder(o.id); after(900, () => { addFeed('wira', 'Warehouse', `${o.id} queued for next FEFO wave`); orderEvent(o.id, 'wira', 'Queued for next FEFO wave'); }); }
        }
        if (sc.escalate) after(400, () => push(id, { type: 'system', text: `${AG[sc.agent].name} handed off to a human · refund above limit` }, { mode: 'copilot', status: 'human', suggestion: sc.suggest, suggestBy: sc.agent }));
      });
    });
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [push, upd, patch, after, addFeed, createOrder, advanceOrder, orderEvent]);

  const sendText = useCallback((text, viaAi) => {
    const s = stateRef.current;
    const c = conv(s, s.activeId);
    if (!text || !text.trim()) return;
    if (c.mode === 'auto') push(c.id, { type: 'system', text: `You replied · ${AG[c.agent].name} paused` }, { mode: 'manual' });
    push(c.id, { type: 'human', author: 'Johan', text, time: now(), viaAi }, { preview: text, time: now(), suggestion: null, unread: 0, status: c.status === 'human' ? 'ongoing' : c.status });
    patch({ draft: '' });
  }, [push, patch]);

  const simS = useCallback((i) => {
    const s = stateRef.current;
    if (s.sTyping) return;
    const sid = s.supId;
    const sup = SUP[sid];
    const sc = SSC[i];
    const f = (t) => t.replace('{sup}', sup.name);
    pushS(sid, { from: 'me', text: sc.label });
    patch({ sTyping: true });
    after(1400, () => { patch({ sTyping: false }); pushS(sid, { from: 'tara', text: f(sc.reply) }); addFeed('tara', 'Suppliers', f(sc.feed)); });
  }, [pushS, patch, after, addFeed]);

  const sendS = useCallback(() => {
    const s = stateRef.current;
    const t = s.sDraft.trim();
    if (!t || s.sTyping) return;
    const sid = s.supId;
    const sup = SUP[sid];
    pushS(sid, { from: 'me', text: t });
    patch({ sDraft: '', sTyping: true });
    after(1400, () => { patch({ sTyping: false }); pushS(sid, { from: 'tara', text: 'Baik, pesan sudah saya terima dan saya teruskan ke buyer Total Buah Segar. Balasan maksimal 1 jam kerja.' }); addFeed('tara', 'Suppliers', `Message from ${sup.name}: "${t}" — forwarded to buyer`); });
  }, [pushS, patch, after, addFeed]);

  const until = useCallback((cond, fn, tries = 60) => {
    if (cond()) { fn(); return; }
    if (tries > 0) after(350, () => until(cond, fn, tries - 1));
  }, [after]);

  const simWhenFree = useCallback((key, convId) => {
    until(() => !stateRef.current.busy, () => { if (!simulate(key, convId)) after(350, () => simWhenFree(key, convId)); });
  }, [until, simulate, after]);

  const storeTicket = useCallback((st) => {
    const id = 'GR-' + seq.current.oseq++;
    const items = (SDEF[st.id] || [['APL', 20], ['PSG', 15]]).map(([sku, qty]) => ({ sku, qty }));
    const o = { id, convId: null, customer: st.name, channel: 'branch', stage: 'paid', time: now(), items, disc: 0, events: [{ agent: 'wira', text: `Transfer ticket raised by ${st.name}`, time: now() }, { agent: 'wira', text: `Approved · stock reserved at ${WHN[st.dc]}`, time: now() }] };
    patch((prev) => ({ orders: [o, ...prev.orders], inv: prev.inv.map((i) => { const l = items.find((x) => x.sku === i.sku); return l ? { ...i, reserved: i.reserved + l.qty } : i; }) }));
    addFeed('wira', 'Stores', `${st.name} raised transfer ${id} · ${itemsStr(stateRef.current, items)} · from ${WHN[st.dc]}`);
    toastMsg(`Wira opened ticket ${id} for ${st.name}`, 'wira');
    after(1400, () => reorderCheck(items.map((l) => l.sku)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patch, addFeed, toastMsg, after]);

  const mayaOrder = useCallback(() => stateRef.current.orders.find((o) => o.convId === 'maya'), []);
  const aplPO = useCallback(() => stateRef.current.pos.find((p) => p.items.some((l) => l.sku === 'APL') && p.status !== 'rejected'), []);

  const setView = useCallback((v) => patch({ view: v }), [patch]);

  // The scripted 9-step walkthrough Scout narrates. Each step's `done` is
  // derived live from state; `run` drives the same actions a person would
  // click, just chained together with `until`/`after` waits.
  const guide = useCallback(() => {
    const s = stateRef.current;
    const mo = mayaOrder();
    const ap = aplPO();
    const p18 = s.pos.find((p) => p.id === 'PO-0418');
    const ex = s.exc.find((e) => e.po === 'PO-0418');
    const staff = (v) => patch({ role: 'staff', view: v });
    return [
      { t: 'A customer orders on WhatsApp', d: 'Maya asks for apel Fuji. Sari checks live DC stock, quotes the promo and builds the order with a QRIS link.', done: !!mo, run: () => { patch({ role: 'staff', view: 'inbox', activeId: 'maya' }); after(300, () => { simWhenFree('stock', 'maya'); after(800, () => simWhenFree('order', 'maya')); }); } },
      { t: 'Payment lands, the DC is told', d: 'Maya pays. Sari confirms the QRIS and queues the order for the next FEFO pick wave.', done: !!mo && mo.stage !== 'new', run: () => { patch({ role: 'staff', view: 'inbox', activeId: 'maya' }); until(() => !!mayaOrder(), () => simWhenFree('paid', 'maya')); } },
      { t: 'Stock hits reorder — Tara drafts a PO', d: 'That order pushed Apel Fuji to its reorder point. Tara forecasts demand and drafts a PO to the importer.', done: !!ap, run: () => { staff('procurement'); after(400, () => { if (!aplPO()) reorderCheck(['APL']); }); } },
      { t: 'The buyer approves in one tap', d: 'The PO is above the auto-send limit, so it waits for a person. Approve it and Tara sends it on WhatsApp.', done: !!ap && ap.status !== 'draft', run: () => { staff('procurement'); after(500, () => { const p = aplPO(); if (p && p.status === 'draft') approvePO(p.id); }); } },
      { t: 'The supplier confirms and dispatches', d: 'Switch to the supplier side. PT Sumber Rejeki confirms the PO and sends a reefer truck.', done: !!ap && ['transit', 'arrived', 'received', 'closed'].includes(ap.status), run: () => { patch({ role: 'supplier', supId: 's1', view: 'portal' }); after(900, () => until(() => aplPO()?.status === 'sent', () => { supConfirm(aplPO().id, false); after(1400, () => until(() => aplPO()?.status === 'confirmed', () => supDispatch(aplPO().id))); })); } },
      { t: 'Track the reefer to the dock', d: 'Back as staff: every truck and rider live, with reefer temperature. The jeruk truck from Priok is about to arrive.', done: !!p18 && ['arrived', 'received', 'closed'].includes(p18.status), run: () => { patch({ role: 'staff', view: 'dispatch', shipF: 'all', selShip: 'SHP-7779' }); after(600, () => fastForward('SHP-7779')); } },
      { t: 'Receiving catches a short-ship', d: 'Wira counts 48 of 60 kg. Tara logs the exception, lowers the supplier score and proposes a claim.', done: !!ex, run: () => { staff('warehouse'); fastForward('SHP-7779'); until(() => stateRef.current.pos.find((x) => x.id === 'PO-0418')?.status === 'arrived', () => { after(500, () => receivePO('PO-0418')); after(2600, () => staff('procurement')); }); } },
      { t: 'Claim back the leakage', d: 'One tap turns the gap into a credit-note claim to the supplier and adds it to recovered leakage.', done: !!ex && ex.status !== 'open', run: () => { staff('procurement'); after(500, () => { const e = stateRef.current.exc.find((x) => x.po === 'PO-0418'); if (e) resolveEx(e.id, 'claimed'); }); } },
      { t: "Pick, QC and deliver Maya's apples", d: 'Wira runs a FEFO wave, Cek grades the packing photo, a rider is booked and Maya gets a tracking link.', done: !!mo && ['shipped', 'delivered'].includes(mo.stage), run: () => { staff('warehouse'); after(400, () => { if (mayaOrder()?.stage === 'paid') pickWave(); }); until(() => mayaOrder()?.stage === 'verified', () => { after(500, () => { advanceOrder(mayaOrder().id); staff('dispatch'); }); }, 80); } },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mayaOrder, aplPO, patch, after, simWhenFree, until, reorderCheck, approvePO, supConfirm, supDispatch, fastForward, receivePO, resolveEx, pickWave, advanceOrder]);

  const actions = {
    reset, after, addFeed, toastMsg, upd, push, setPO, orderEvent, pushS,
    createOrder, createOutShip, advanceOrder, pickWave, reorderCheck, poFlow,
    approvePO, rejectPO, supConfirm, supDispatch, receivePO, resolveEx,
    fastForward, setMode, simulate, sendText, simS, sendS, until, simWhenFree,
    storeTicket, mayaOrder, aplPO, setView, patch, guide,
  };

  return { state, actions, threadRef, sRef, helpers: { item, avail, totals, poTotal, conv, pendingOrder, itemsStr, stLabel } };
}
